import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal } from "../components/ui/Modal";
import { useRaiseShopifyImportCeiling, useRetryFailedShopifyImport, useShopifyImportJob, useShopifyImportJobs } from "../hooks/useMedia";
import { ApiError } from "../lib/api";
import { formatCents, timeAgo } from "../lib/utils";
import type { CrawlJob } from "../../../shared/crawl";

function statusTone(status: CrawlJob["status"]) {
  switch (status) {
    case "done":
      return "border-emerald-200 bg-emerald-50 text-emerald-900";
    case "partial_success":
      return "border-amber-200 bg-amber-50 text-amber-900";
    case "failed":
      return "border-red-200 bg-red-50 text-red-900";
    default:
      return "border-blue-200 bg-blue-50 text-blue-900";
  }
}

function importPhaseSummary(job: CrawlJob) {
  if (job.status === "queued") {
    return {
      title: "Downloading JSONL",
      detail: "Waiting on Shopify bulk output and queue pickup.",
    };
  }
  if (job.status === "crawling") {
    const products = job.product_count_actual ?? job.pages_attempted ?? 0;
    const estimatedProducts = job.product_count_estimated ?? "…";
    const images = job.image_count_actual ?? 0;
    const estimatedImages = job.image_count_estimated ?? "…";
    return {
      title: "Processing catalog",
      detail: `Products ${products} of ${estimatedProducts}; images ${images} of ${estimatedImages}.`,
    };
  }
  if (job.status === "done") {
    return {
      title: "Import complete",
      detail: "Catalog knowledge and media assets are ready.",
    };
  }
  if (job.status === "partial_success") {
    return {
      title: "Import completed with recoverable issues",
      detail: "Some items imported successfully, but one or more products or images need attention.",
    };
  }
  return {
    title: "Import failed",
    detail: "The import stopped before completing successfully.",
  };
}

function progressSteps(job: CrawlJob) {
  const products = job.product_count_actual ?? job.pages_attempted ?? 0;
  const estimatedProducts = job.product_count_estimated ?? null;
  const images = job.image_count_actual ?? 0;
  const estimatedImages = job.image_count_estimated ?? null;

  return [
    {
      key: "download",
      label: "Downloading JSONL",
      detail: job.status === "queued"
        ? "Waiting on Shopify bulk output"
        : (job.status === "failed" && !job.product_count_actual ? "Download failed" : "Download complete"),
      active: job.status === "queued",
      done: job.status !== "queued" && !(job.status === "failed" && !job.product_count_actual),
      failed: job.status === "failed" && !job.product_count_actual,
    },
    {
      key: "products",
      label: "Processing products",
      detail: estimatedProducts != null ? `${products} of ${estimatedProducts}` : `${products} processed`,
      active: job.status === "crawling",
      done: products > 0 || job.status === "done" || job.status === "partial_success",
    },
    {
      key: "images",
      label: "Processing images",
      detail: estimatedImages != null ? `${images} of ${estimatedImages}` : `${images} imported`,
      active: job.status === "crawling" && images > 0,
      done: images > 0 || job.status === "done" || job.status === "partial_success",
    },
  ];
}

function ImportProgressSteps({ job }: { job: CrawlJob }) {
  const steps = progressSteps(job);
  return (
    <div className="mt-5 grid gap-3 md:grid-cols-3">
      {steps.map((step) => {
        const tone = step.active
          ? "border-blue-300 bg-white/80 text-blue-950"
          : step.failed
            ? "border-red-200 bg-white/80 text-red-900"
          : step.done
            ? "border-emerald-200 bg-white/80 text-emerald-950"
            : "border-slate-200 bg-white/60 text-slate-600";
        return (
          <div key={step.key} className={`rounded-xl border p-4 ${tone}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] opacity-80">{step.label}</p>
            <p className="mt-2 text-lg font-semibold">{step.detail}</p>
          </div>
        );
      })}
    </div>
  );
}

export function ShopifyImportListPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const importsQuery = useShopifyImportJobs(brandId);
  const jobs = importsQuery.data?.data ?? [];

  if (!brandId) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        Choose a brand to view Shopify imports.
      </div>
    );
  }

  if (importsQuery.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (importsQuery.isError) {
    const message = importsQuery.error instanceof Error ? importsQuery.error.message : "Could not load Shopify imports right now.";
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        {message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Shopify imports</p>
          <h2 className="mt-1 text-2xl font-semibold text-slate-950">Recent catalog imports</h2>
        </div>
        <Link
          to={`/brand/${brandId}/assets`}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
        >
          Back to Asset Library
        </Link>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        {jobs.length ? (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/brand/${brandId}/assets/imports/${job.id}`}
                className="block rounded-xl border border-slate-200 p-4 hover:border-slate-400"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{job.shop || "Connected Shopify store"}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {job.status.replace("_", " ")} · {timeAgo(job.created_at)} · cost {formatCents(job.actual_cost_cents ?? job.estimated_cost_cents ?? 0)}
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${statusTone(job.status)}`}>
                    {job.status.replace("_", " ")}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-600">No Shopify imports yet.</p>
        )}
      </section>
    </div>
  );
}

export function ShopifyImportDetailPage() {
  const navigate = useNavigate();
  const { brandId, jobId } = useParams<{ brandId: string; jobId: string }>();
  const importJob = useShopifyImportJob(brandId, jobId);
  const retryFailed = useRetryFailedShopifyImport(brandId ?? "");
  const raiseCeiling = useRaiseShopifyImportCeiling(brandId ?? "");
  const job = importJob.data?.data;
  useEffect(() => {
    if (brandId && job?.refreshed_by && job.refreshed_by !== jobId) {
      navigate(`/brand/${brandId}/assets/imports/${job.refreshed_by}`, { replace: true });
    }
  }, [brandId, job?.refreshed_by, jobId, navigate]);
  const retryError = retryFailed.error instanceof ApiError ? retryFailed.error : null;
  const raiseError = raiseCeiling.error instanceof ApiError ? raiseCeiling.error : null;
  const retryableErrors = (job?.errors ?? []).filter((error) => error.target.startsWith("image:") || error.target.startsWith("product:"));
  const retryMeta = retryFailed.data?.meta;
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [showRetryBudgetModal, setShowRetryBudgetModal] = useState(false);
  const retryBudgetDetails = retryError?.code === "IMPORT_COST_CEILING_EXCEEDED" && retryError.details && typeof retryError.details === "object"
    ? retryError.details as {
        suggested_ceiling_cents?: number;
        estimated_retry_cost_cents?: number;
        current_ceiling_cents?: number;
        original_ceiling_cents?: number;
        charged_so_far_cents?: number;
      }
    : null;

  useEffect(() => {
    if (retryBudgetDetails) {
      setShowRetryBudgetModal(true);
    }
  }, [retryBudgetDetails]);

  useEffect(() => {
    if (raiseCeiling.isSuccess) {
      setShowRetryBudgetModal(false);
    }
  }, [raiseCeiling.isSuccess]);

  if (!brandId) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        Choose a brand to view this Shopify import.
      </div>
    );
  }

  if (importJob.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        Could not load this Shopify import job.
      </div>
    );
  }

  const phase = importPhaseSummary(job);
  const visibleErrors = showAllErrors ? (job.errors ?? []) : (job.errors ?? []).slice(0, 5);

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Shopify import</p>
            <h2 className="mt-1 text-2xl font-semibold text-slate-950">{job.shop || "Connected Shopify store"}</h2>
            <p className="mt-2 text-sm text-slate-600">Started {timeAgo(job.created_at)}. This page refreshes while the import is active.</p>
          </div>
          <Link
            to={`/brand/${brandId}/assets/imports`}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Back to imports
          </Link>
        </div>

        <section className={`rounded-2xl border p-5 ${statusTone(job.status)}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-80">Status</p>
              <h3 className="mt-1 text-xl font-semibold capitalize">{job.status.replace("_", " ")}</h3>
              <p className="mt-2 text-sm opacity-80">{phase.title}</p>
              <p className="mt-1 text-sm opacity-80">{phase.detail}</p>
            </div>
            <div className="text-sm opacity-80">
              {job.error || "No blocking errors reported"}
            </div>
          </div>
          <ImportProgressSteps job={job} />
          {retryError && !retryBudgetDetails ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-white/70 p-4 text-sm text-red-800">
              {retryError.message}
            </div>
          ) : null}
          {raiseError ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-white/70 p-4 text-sm text-red-800">
              {raiseError.message}
            </div>
          ) : null}
          {retryableErrors.length > 0 ? (
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!jobId || retryFailed.isPending) return;
                  retryFailed.mutate(jobId, { onSuccess: (result) => {
                    navigate(`/brand/${brandId}/assets/imports/${result.data.id}`, { replace: true });
                  } });
                }}
                disabled={!jobId || retryFailed.isPending || job.status === 'queued' || job.status === 'crawling'}
                className="rounded-lg border border-slate-300 bg-white/70 px-4 py-2 text-sm font-medium text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {retryFailed.isPending ? "Retrying failed items…" : `Retry failed (${retryableErrors.length})`}
              </button>
              <span className="text-sm opacity-80">
                Retries failed Shopify product and image targets from this import. Image retries are capped at 50 per click.
                {typeof retryMeta?.remaining_retryable_image_count === "number" && retryMeta.remaining_retryable_image_count > 0
                  ? ` ${retryMeta.remaining_retryable_image_count} failed image${retryMeta.remaining_retryable_image_count === 1 ? "" : "s"} still remain after this batch.`
                  : ""}
                {typeof retryMeta?.remaining_retryable_product_count === "number" && retryMeta.remaining_retryable_product_count > 0
                  ? ` ${retryMeta.remaining_retryable_product_count} failed product${retryMeta.remaining_retryable_product_count === 1 ? "" : "s"} still remain after this batch.`
                  : ""}
              </span>
            </div>
          ) : null}
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Estimated products</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{job.product_count_estimated ?? "—"}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Processed products</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{job.product_count_actual ?? job.pages_attempted ?? 0}</p>
          </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Estimated images</p>
          <p className="mt-2 text-2xl font-semibold text-slate-950">{job.image_count_estimated ?? "—"}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Imported images</p>
          <p className="mt-2 text-2xl font-semibold text-slate-950">{job.image_count_actual ?? 0}</p>
        </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Raw cost</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{formatCents(job.actual_cost_cents ?? job.estimated_cost_cents ?? 0)}</p>
            <p className="mt-1 text-xs text-slate-500">Ceiling {formatCents(job.cost_ceiling_cents ?? 0)}</p>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Job details</p>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">Bulk operation</dt>
                <dd className="mt-1 break-all text-sm text-slate-900">{job.shopify_bulk_operation_id}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Collection</dt>
                <dd className="mt-1 text-sm text-slate-900">
                  {job.collection_id ? (
                    <Link className="text-blue-700 underline-offset-2 hover:underline" to={`/brand/${brandId}/assets?collection_id=${job.collection_id}`}>
                      {job.collection_id}
                    </Link>
                  ) : "Pending"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Queue status</dt>
                <dd className="mt-1 text-sm text-slate-900">{job.shopify_bulk_op_status || "Waiting for Shopify"}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Completed</dt>
                <dd className="mt-1 text-sm text-slate-900">{job.completed_at ? timeAgo(job.completed_at) : "Still running"}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Errors</p>
            {job.errors?.length ? (
              <div className="mt-4 space-y-3">
                {visibleErrors.map((error, index) => (
                  <div key={`${error.target}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-medium text-slate-900">{error.target}</p>
                    <p className="mt-1 text-sm text-slate-600">{error.message}</p>
                  </div>
                ))}
                {job.errors.length > 5 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllErrors((value) => !value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"
                  >
                    {showAllErrors ? "Show fewer errors" : "View all errors"}
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-600">No itemized errors recorded yet.</p>
            )}
          </div>
        </section>
      </div>

      <Modal
        open={showRetryBudgetModal}
        onClose={() => setShowRetryBudgetModal(false)}
        title="Raise Retry Ceiling"
      >
        <div className="space-y-4">
          {retryError ? (
            <p className="text-sm font-medium text-red-700">
              {retryError.message}
            </p>
          ) : null}
          <p className="text-sm text-slate-700">
            This retry batch would exceed the import’s confirmed ceiling. Raise the ceiling to continue retrying the failed items.
          </p>
          <dl className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Current ceiling</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatCents(retryBudgetDetails?.current_ceiling_cents ?? retryBudgetDetails?.original_ceiling_cents ?? job.cost_ceiling_cents ?? 0)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Charged so far</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatCents(retryBudgetDetails?.charged_so_far_cents ?? job.actual_cost_cents ?? 0)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Retry batch estimate</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatCents(retryBudgetDetails?.estimated_retry_cost_cents ?? 0)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Proposed ceiling</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatCents(retryBudgetDetails?.suggested_ceiling_cents ?? job.cost_ceiling_cents ?? 0)}
              </dd>
            </div>
          </dl>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowRetryBudgetModal(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (!jobId || !retryBudgetDetails?.suggested_ceiling_cents || raiseCeiling.isPending) return;
                raiseCeiling.mutate({
                  jobId,
                  new_ceiling_cents: retryBudgetDetails.suggested_ceiling_cents,
                });
              }}
              disabled={!jobId || !retryBudgetDetails?.suggested_ceiling_cents || raiseCeiling.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {raiseCeiling.isPending ? "Raising ceiling…" : `Raise ceiling to ${formatCents(retryBudgetDetails?.suggested_ceiling_cents ?? job.cost_ceiling_cents ?? 0)}`}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
